/* eslint-disable camelcase */
import React, { useState } from 'react';

import Paragraph from 'components/text/Paragraph';
import PropTypes from 'prop-types';
import { above } from 'utils/mediaqueries';
import colors from 'config/theme/colors';
import styled from 'libs/styled';
import { useTranslation } from 'react-i18next';

const GiftCertificateSelectorWrapper = styled('div')`
    display: flex;
    flex-wrap: wrap;
    margin: -6px;

    &.disabled {
        opacity: 0.6;
        cursor: not-allowed;

        & > * {
            pointer-events: none;
        }
    }
`;

const AmountButton = styled('button')`
    display: flex;
    justify-content: center;
    align-items: center;
    width: calc(50% - 12px);
    margin: 6px;
    padding: 14px;
    border: 1px solid ${colors.black};
    font-size: 16px;
    outline: none;

    ${above.md} {
        width: calc(33.33% - 12px);
    }

    &:hover,
    &.selected {
        background-color: ${colors.black};
        color: ${colors.white};
    }
`;

const AmountInput = styled('input')`
    width: calc(100% - 12px);
    margin: 26px 6px 6px 6px;
    border: none;
    border-bottom: 1px solid ${colors.black};
    border-radius: 0;
    outline: none;
    font-size: 16px;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button,
    &[type='number'] {
        -moz-appearance: textfield;
        -webkit-appearance: none;
    }
`;

const Error = styled(Paragraph)`
    margin: 16px 6px 0;
    color: ${colors.error};
    font-size: 16px;
    line-height: 1.2;

    a {
        color: inherit;
    }
`;

const GiftCertificateSelector = ({
    disabled = false,
    flexibleGiftCertPlaceholder,
    giftCertificates,
    maxValue,
    minValue = 0,
    onSelect,
    selectedId,
}) => {
    let timeout;
    const { t } = useTranslation();
    const handleClick = cert => {
        if (!disabled) {
            // If the clicked gift certificate is anyAmount it means we should open the number-input
            if (cert.type === 'anyAmount') {
                onSelect({ ...cert, flexibleAmount: null });
                setFlexAmountCert(cert);

                // else we can select the clicked card
            } else {
                onSelect(cert);
                setErrorMessage(null);
                setFlexAmountCert(null);
            }
        }
    };

    const handleAmountInput = e => {
        if (!disabled) {
            const inputValue = e.target.value;
            clearTimeout(timeout);

            // Save the value when the user has stoped typeing
            timeout = setTimeout(() => {
                const flexibleAmount = parseInt(inputValue, 0);

                const valueToLow = flexibleAmount < minValue || flexibleAmount === 0;
                if (valueToLow) {
                    setErrorMessage(t('checkout_gift_certificate.too_low_amount'));
                    onSelect({
                        ...flexAmountCert,
                        flexibleAmount: null,
                    });

                    return;
                }

                const valueToHigh = maxValue && flexibleAmount > maxValue;
                if (valueToHigh) {
                    setErrorMessage(t('checkout_gift_certificate.too_high_amount'));
                    onSelect({
                        ...flexAmountCert,
                        flexibleAmount: null,
                    });

                    return;
                }

                const isNumber = isNaN(flexibleAmount);
                if (isNumber) {
                    setErrorMessage(t('checkout_gift_certificate.invalid_input'));
                    onSelect({
                        ...flexAmountCert,
                        flexibleAmount: null,
                    });

                    return;
                }

                // Clear error messages if we had any before
                if (errorMessage) {
                    setErrorMessage(null);
                }

                // Select the given gift certificate, and add the flexibleAmount-value
                onSelect({
                    ...flexAmountCert,
                    flexibleAmount,
                });
            }, 350);
        }
    };

    const [flexAmountCert, setFlexAmountCert] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    return (
        <GiftCertificateSelectorWrapper className={disabled ? 'disabled' : null}>
            {giftCertificates.map((cert, index) => (
                <AmountButton
                    className={cert.id === selectedId ? 'selected' : null}
                    key={`${cert.id}-${index}`}
                    onClick={() => handleClick(cert)}
                >
                    {cert.name}
                </AmountButton>
            ))}
            {flexAmountCert && (
                <>
                    <AmountInput
                        type="number"
                        max={typeof maxValue === 'number' ? maxValue : undefined}
                        min={typeof minValue === 'number' ? minValue : undefined}
                        placeholder={flexibleGiftCertPlaceholder}
                        onChange={e => handleAmountInput(e)}
                    />
                    {errorMessage && <Error>{errorMessage}</Error>}
                </>
            )}
        </GiftCertificateSelectorWrapper>
    );
};

GiftCertificateSelector.propTypes = {
    disabled: PropTypes.bool,
    flexibleGiftCertPlaceholder: PropTypes.string.isRequired,
    giftCertificates: PropTypes.arrayOf(
        PropTypes.exact({
            id: PropTypes.string,
            name: PropTypes.string,
            type: PropTypes.string,
        })
    ).isRequired,
    maxValue: PropTypes.number,
    minValue: PropTypes.number,
    onSelect: PropTypes.func.isRequired,
    selectedId: PropTypes.string,
};

export default GiftCertificateSelector;
